import type {
  ExternalReference,
  LineItemSupplier,
  PatientType,
} from "elephant-web/gql";
import type { InvoiceStatus } from "../InvoiceFilters/types";
import type {
  InsuranceDetails,
  Insurer,
  Payer,
} from "./InsurerAccordion/types";

export interface Payers {
  tariffId?: string;
  name?: string;
  amountInPence?: number;
  revenueDepartment?: string;
  revenueDepartmentId?: string;
}

export enum PriceListItemType {
  PROCEDURE = "Procedure",
  CONSULTATION = "Consultation",
  PRESCRIPTION = "Prescription",
  INVESTIGATION = "Investigation",
  INVENTORY = "Inventory",
  OTHER = "Other",
}

export interface PayerSummary {
  type: string;
  name: string;
  balance: {
    paid: number;
    due?: number;
  };
}

interface InvoiceCardSummaryLocation {
  id: string;
  name: string;
}

export interface InvoiceCardSummaryInfo {
  invoiceNumber: string;
  invoiceStatus: InvoiceStatus;
  patientType: string;
  created: string;
  creator: string;
  balance: string;
  total: string;
  payers: PayerSummary[];
  facility: InvoiceCardSummaryLocation;
  organisation: InvoiceCardSummaryLocation;
}

export interface InvoiceCardSummaryProps extends InvoiceCardSummaryInfo {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

// The price-list panel only ever fetches the fields needed to surface items
// to the user; `cost` and `priceListItemType` live on line-items themselves
// (populated by the server when an item is added to an invoice).
export type PriceListItemRow = Omit<
  PriceListItem,
  "cost" | "priceListItemType"
>;

export interface PriceList {
  priceListItems: PriceListItemRow[];
}

export interface PriceListItem {
  uuid?: string;
  name: string;
  priceListItemType: PriceListItemType;
  cost: number;
  payers: Payers[];
  activeTariffId?: string;
  revenueDepartment?: string;
  externalReference?: ExternalReference;
}

export interface LineItem {
  id?: string;
  tempId?: string;
  priceListItem: PriceListItem;
  quantity: number;
  cost: number;
  formattedPrice?: string;
  formattedTotal?: string;
  disabled: boolean;
  automated: boolean;
  incurredAt?: Date;
  supplier?: LineItemSupplier;
  missingPrice?: boolean;
}

export interface Balance {
  total: number;
  paid: number;
  due: number;
  currency: string;
}

export interface PatientInsurance {
  insuranceType: string;
  insuranceDetails: InsuranceDetails;
}
export interface Patient {
  insurance: PatientInsurance;
}
export interface InvoiceCardDetailsInfo {
  invoiceStatus: InvoiceStatus;
  currencyCode: string;
  patientType: PatientType;
  lineItems: LineItem[];
  payers: Payer[];
  patient: Patient;
  total: number;
  paymentIsDue: boolean;
  hasPatientPayer: boolean;
}
export interface InvoiceCardDetailsProps extends InvoiceCardDetailsInfo {
  deleteLineItem?: () => void;
}

export interface Invoice {
  invoiceId: string;
  invoiceSummary: InvoiceCardSummaryInfo;
}
export interface InvoiceCardProps extends Invoice {
  priceList: PriceList;
  insurers: Insurer[];
  balance: { total?: number; due?: number };
}

export interface InvoiceDetailsProps {
  invoiceId: string;
  priceList: PriceList;
  insurers: Insurer[];
  setSummaryBalance: (balance: string) => void;
  setSummaryTotal: (total: string) => void;
  isNewInvoice: boolean;
  invoiceStatus: InvoiceStatus;
  facility: InvoiceCardSummaryLocation;
}

export interface PrintableLaserReceiptProps {
  invoiceId: string;
  onAfterPrint: () => void;
}
